import { useQuery } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithStorage, createJSONStorage, RESET } from 'jotai/utils';
import { useCallback } from 'react';
import { queryMe } from '../me/api';
import type { ProductAnalysisInputDto } from './model';
import {
  getProductAnalysisResultDraftStorageKey,
  isProductAnalysisResultDraft,
  type ProductAnalysisResultDraft,
} from './storage';

const unknown_session_storage = createJSONStorage<unknown>(
  () => sessionStorage,
);
const session_storage = {
  getItem: (key: string, initial_value: ProductAnalysisResultDraft) => {
    const value = unknown_session_storage.getItem(key, initial_value);
    return isProductAnalysisResultDraft(value) ? value : initial_value;
  },
  setItem: (key: string, value: ProductAnalysisResultDraft) =>
    unknown_session_storage.setItem(key, value),
  removeItem: (key: string) => unknown_session_storage.removeItem(key),
};

const product_analysis_page_data_family_atom = atomFamily((userId: string) =>
  atomWithStorage<Partial<ProductAnalysisInputDto>>(
    `${userId}_v2_product_analysis_page_data`,
    {
      product_name: '',
      product_images: null!,
      region: '',
      skill_id: null!,
      duration: null!,
      extra_prompt: '',
    },
    createJSONStorage(() => sessionStorage),
    { getOnInit: true },
  ),
);

export function useProductAnalysisPageData() {
  const { data } = useQuery(queryMe);
  return useAtom(product_analysis_page_data_family_atom(data.name));
}

const product_analysis_result_draft_family_atom = atomFamily(
  (storage_key: string) =>
    atomWithStorage<ProductAnalysisResultDraft>(
      storage_key,
      {},
      session_storage,
      { getOnInit: true },
    ),
);

function useProductAnalysisResultDraftAtom(task_id: string) {
  const { data } = useQuery(queryMe);
  return product_analysis_result_draft_family_atom(
    getProductAnalysisResultDraftStorageKey(data.name, task_id),
  );
}

export function useProductAnalysisResultDraft(task_id: string) {
  return useAtom(useProductAnalysisResultDraftAtom(task_id));
}

export function useResetProductAnalysisResultDraft(task_id: string) {
  const set_draft = useSetAtom(useProductAnalysisResultDraftAtom(task_id));

  return useCallback(() => set_draft(RESET), [set_draft]);
}

const productAnalysisCurrentTaskIdFamilyAtom = atomFamily((accountId: string) =>
  atomWithStorage<string | null>(
    `${accountId}_v1_product_analysis_current_task_id`,
    null,
    createJSONStorage<string | null>(() => sessionStorage),
    { getOnInit: true },
  ),
);

export function useProductAnalysisCurrentTaskId() {
  const { data } = useQuery(queryMe);
  return useAtom(productAnalysisCurrentTaskIdFamilyAtom(data.name));
}
